import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel
} from '@mui/material';
import { useDataProvider, useNotify, Title, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { MaterialSymbol } from '../common/MaterialSymbol';
import tokenService from '../../services/tokenService';
import './course-management-design.css';

interface CourseCatalog {
    id: number;
    name: string;
    description?: string;
    isActive: boolean;
    companyId?: number;
    createdAt: string;
    courses?: any[];
}

type StatusFilter = 'aktiv' | 'inaktiv';
type SortOption = 'name' | 'count' | 'date';

const CourseCatalogs: React.FC = () => {
    const [catalogs, setCatalogs] = useState<CourseCatalog[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCatalog, setEditingCatalog] = useState<CourseCatalog | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isActive: true
    });

    // Rein clientseitiger Anzeige-Zustand (Vorlage kursverwaltung-v2, View kvCatalogs)
    const [search, setSearch] = useState('');
    const [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
    const [sortBy, setSortBy] = useState<SortOption>('name');
    const [view, setView] = useState<'grid' | 'list'>('grid');

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const translate = useTranslate();

    useEffect(() => {
        loadCatalogs();
    }, []);

    const loadCatalogs = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('course_catalogs', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: { companyId: tokenService.getCompanyId() }
            });
            setCatalogs(data as CourseCatalog[]);
        } catch (error) {
            notify(translate('catalogs.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (catalog?: CourseCatalog) => {
        if (catalog) {
            setEditingCatalog(catalog);
            setFormData({
                name: catalog.name,
                description: catalog.description || '',
                isActive: catalog.isActive
            });
        } else {
            setEditingCatalog(null);
            setFormData({
                name: '',
                description: '',
                isActive: true
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingCatalog(null);
    };

    const handleSave = async () => {
        try {
            if (editingCatalog) {
                await dataProvider.update('course_catalogs', {
                    id: editingCatalog.id,
                    data: formData,
                    previousData: editingCatalog
                });
                notify(translate('catalogs.updateSuccess'), { type: 'success' });
            } else {
                await dataProvider.create('course_catalogs', {
                    data: formData
                });
                notify(translate('catalogs.createSuccess'), { type: 'success' });
            }
            loadCatalogs();
            handleCloseDialog();
        } catch (error: any) {
            // Parse API Platform error response for better user feedback
            let errorMessage = translate('catalogs.saveError');

            if (error?.body) {
                // API Platform validation errors
                if (error.body['hydra:description']) {
                    errorMessage = error.body['hydra:description'];
                } else if (error.body.detail) {
                    errorMessage = error.body.detail;
                } else if (error.body.message) {
                    errorMessage = error.body.message;
                }

                // Check for unique constraint violation on name or slug
                if (errorMessage.includes('name') || errorMessage.includes('slug') || errorMessage.includes('Duplicate') || errorMessage.includes('unique') || errorMessage.includes('1062')) {
                    errorMessage = `Ein Katalog mit dem Namen "${formData.name}" existiert bereits. Bitte wählen Sie einen anderen Namen.`;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            notify(errorMessage, { type: 'error' });
            console.error('Catalog save error details:', error);
            // Don't close dialog - let user fix the error
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(translate('catalogs.confirmDelete'))) {
            return;
        }

        try {
            await dataProvider.delete('course_catalogs', {
                id,
                previousData: {} as any
            });
            notify(translate('catalogs.deleteSuccess'), { type: 'success' });
            loadCatalogs();
        } catch (error) {
            notify(translate('catalogs.deleteError'), { type: 'error' });
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const toggleFilter = <T,>(list: T[], value: T): T[] =>
        list.includes(value) ? list.filter(v => v !== value) : [...list, value];

    const resetFilters = () => {
        setStatusFilters([]);
        setSearch('');
    };

    const filterChipLabels: Record<string, string> = {
        aktiv: translate('catalogs.active'),
        inaktiv: translate('catalogs.inactive')
    };

    const visibleCatalogs = useMemo(() => {
        const q = search.trim().toLowerCase();
        const result = catalogs.filter(catalog => {
            const status: StatusFilter = catalog.isActive ? 'aktiv' : 'inaktiv';
            if (statusFilters.length > 0 && !statusFilters.includes(status)) return false;
            if (q && !catalog.name.toLowerCase().includes(q)) return false;
            return true;
        });
        result.sort((a, b) => {
            if (sortBy === 'count') return (b.courses?.length || 0) - (a.courses?.length || 0);
            if (sortBy === 'date') return (b.createdAt || '').localeCompare(a.createdAt || '');
            return a.name.localeCompare(b.name, 'de');
        });
        return result;
    }, [catalogs, search, statusFilters, sortBy]);

    const activeFilterChips: string[] = [...statusFilters];

    if (loading) {
        return (
            <div className="oa-course-mgmt">
                <Title title={translate('catalogs.title')} />
                <p className="cat-empty">{translate('catalogs.loading')}</p>
            </div>
        );
    }

    return (
        <div className="oa-course-mgmt">
            <Title title={translate('catalogs.title')} />

            <div className="cat2">
                <aside className="cat2-side">
                    <div className="fil-group">
                        <h4>Status</h4>
                        {(['aktiv', 'inaktiv'] as StatusFilter[]).map(status => (
                            <label className="fil" key={status}>
                                <input
                                    type="checkbox"
                                    checked={statusFilters.includes(status)}
                                    onChange={() => setStatusFilters(prev => toggleFilter(prev, status))}
                                />
                                <span className="box"><MaterialSymbol name="check" size={12} /></span>
                                {filterChipLabels[status]}
                            </label>
                        ))}
                    </div>
                    <button type="button" className="fil-reset" onClick={resetFilters}>
                        Filter zurücksetzen
                    </button>
                </aside>

                <section className="cat2-main">
                    <div className="cat2-toolbar">
                        <div className="cat2-search">
                            <MaterialSymbol name="search" size={18} />
                            <input
                                type="text"
                                placeholder="Kataloge suchen …"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="cat2-tools">
                            <select
                                className="kv2-sort"
                                aria-label="Sortierung"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                            >
                                <option value="name">Name (A–Z)</option>
                                <option value="count">Kurse (meiste)</option>
                                <option value="date">Erstellt (neueste)</option>
                            </select>
                            <div className="viewtoggle">
                                <button
                                    type="button"
                                    className={view === 'grid' ? 'active' : ''}
                                    aria-label="Raster"
                                    onClick={() => setView('grid')}
                                >
                                    <MaterialSymbol name="dashboard" size={18} />
                                </button>
                                <button
                                    type="button"
                                    className={view === 'list' ? 'active' : ''}
                                    aria-label="Liste"
                                    onClick={() => setView('list')}
                                >
                                    <MaterialSymbol name="format_list_bulleted" size={18} />
                                </button>
                            </div>
                            <button type="button" className="btn-solid" onClick={() => handleOpenDialog()}>
                                <MaterialSymbol name="add" size={16} />
                                {translate('catalogs.newCatalog')}
                            </button>
                        </div>
                    </div>

                    <div className="cat2-meta">
                        <span className="kv2-count">
                            {visibleCatalogs.length === 1 ? '1 Eintrag' : `${visibleCatalogs.length} Einträge`}
                        </span>
                        <div className="chips">
                            {activeFilterChips.map(value => (
                                <button
                                    type="button"
                                    className="ac"
                                    key={value}
                                    onClick={() => setStatusFilters(prev => prev.filter(v => v !== value))}
                                >
                                    {filterChipLabels[value] || value}
                                    <MaterialSymbol name="close" size={12} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`kv2-grid${view === 'list' ? ' list' : ''}`}>
                        {visibleCatalogs.map((catalog) => (
                            <article className="kvc-card" key={catalog.id}>
                                <div className="kvc-top">
                                    <h3>{catalog.name}</h3>
                                    <div className="kvc-tools">
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label={translate('ra.action.edit')}
                                            title={translate('ra.action.edit')}
                                            onClick={() => handleOpenDialog(catalog)}
                                        >
                                            <MaterialSymbol name="edit" />
                                        </button>
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label={translate('ra.action.delete')}
                                            title={translate('ra.action.delete')}
                                            onClick={() => handleDelete(catalog.id)}
                                        >
                                            <MaterialSymbol name="delete" />
                                        </button>
                                    </div>
                                </div>

                                {catalog.description && (
                                    <p className="kvc-desc">{catalog.description}</p>
                                )}

                                <div className="kvc-chips">
                                    <span className={catalog.isActive ? 'chip-aktiv' : 'chip-inaktiv'}>
                                        {catalog.isActive ? translate('catalogs.active') : translate('catalogs.inactive')}
                                    </span>
                                    <span className="chip-count">
                                        <MaterialSymbol name="layers" size={13} />
                                        {translate('catalogs.coursesCount', { count: catalog.courses?.length || 0 })}
                                    </span>
                                </div>

                                <div className="kvc-meta">
                                    {translate('catalogs.created')}: {formatDate(catalog.createdAt)}
                                </div>

                                <button
                                    type="button"
                                    className="kvc-manage"
                                    onClick={() => navigate(`/courses/catalog/${catalog.id}`)}
                                >
                                    {translate('catalogs.manageCourses')}
                                </button>
                            </article>
                        ))}
                    </div>

                    {visibleCatalogs.length === 0 && (
                        <p className="cat-empty">
                            {catalogs.length === 0
                                ? 'Noch keine Kataloge vorhanden.'
                                : 'Keine Kataloge entsprechen den Filtern.'}
                        </p>
                    )}
                </section>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCatalog ? translate('catalogs.editCatalog') : translate('catalogs.newCatalog')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label={translate('catalogs.form.name')}
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />

                        <TextField
                            label={translate('catalogs.form.description')}
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            }
                            label={translate('catalogs.form.active')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{translate('ra.action.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.name.trim()}>
                        {translate('ra.action.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CourseCatalogs;
